import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Grid,
    Card,
    Divider,
    CardContent,
    IconButton,
    Avatar,
    Chip
} from "@mui/material";
import moment from "moment";
import { RxCross2 } from "react-icons/rx";
import StatusBadge from "components/badge/StatusBadge";
import { statusOptions, capaStatusOptions, incidentSeverityOptions, incidentTypeOptions, contractorOptions } from "helpers/incidentOption";

const ViewIncident = (props) => {
    const { open, handleClose, rowData, isLoading } = props;

    const labelStyle = {
        fontSize: "14px",
        color: "#6b7280",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        mb: 0.5,
    };

    const valueStyle = {
        fontSize: "15px",
        color: "#111827",
        fontWeight: 500,
        mb: 2,
    };

    const getAvatarColor = (text) => {
        const colors = ["#1976D2", "#388E3C", "#F57C00", "#7B1FA2", "#0288D1", "#C2185B"];
        const index = text ? text.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    const getOptionLabel = (value, options) => {
        if (!value) return "N/A";
        const option = options.find(opt => opt.value === value);
        return option ? option.label : value;
    };

    const formatTime = (timeString) => {
        if (!timeString) return "N/A";
        return moment(timeString, "HH:mm:ss").format("hh:mm A");
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    backgroundColor: "#FFFFFF",
                    width: "100%",
                    overflowX: "hidden"
                },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
                position: "relative",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                        sx={{
                            width: 40,
                            height: 40,
                            bgcolor: getAvatarColor(rowData?.customId || ""),
                            color: "#fff",
                            fontSize: "16px",
                            fontWeight: "bold",
                        }}
                    >
                        {rowData?.customId?.[0] || "H"}
                    </Avatar>
                    <Typography sx={{
                        fontWeight: 600,
                        fontSize: "27px",
                        lineHeight: "100%",
                        color: "#140F36",
                        fontFamily: "Outfit, sans-serif"
                    }}>
                        View Incident Details
                    </Typography>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose()}
                    sx={(theme) => ({
                        position: "absolute",
                        right: 50,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <RxCross2 style={{ fontSize: "30px", color: "#140F36" }} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid size={{ md: 12, xs: 12 }}>
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: "3px",
                                border: "1px solid #e5e7eb",
                                backgroundColor: "#fff",
                                p: 2,
                            }}
                        >

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1,
                                }}
                            >
                                <Typography sx={{
                                    fontWeight: 600,
                                    fontSize: "20px",
                                    fontFamily: "Outfit, sans-serif",
                                    color: "#0A0A0C"
                                }}>
                                    Incident Information
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            <CardContent sx={{ pt: 0 }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12 }}>
                                        <Grid container spacing={1}>
                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>HSE ID:</strong>
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: 'center', gap: 1, mb: 2, p: 0 }}>
                                                    <Typography sx={{
                                                        ...valueStyle,
                                                        color: "#1976D2",
                                                        fontWeight: 600
                                                    }}>
                                                        {rowData?.customId ?? "N/A"}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Contractor Name:</strong>
                                                </Typography>
                                                <Typography sx={valueStyle}>
                                                    {rowData?.contractorName}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Type of Incident</strong>
                                                </Typography>
                                                <Chip
                                                    label={rowData?.typeOfIncident}
                                                    sx={valueStyle}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Location:</strong>
                                                </Typography>
                                                <Typography sx={valueStyle}>
                                                    {rowData?.location}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Description of Incident:</strong>
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: "16px",
                                                    color: "#111827",
                                                    fontWeight: 500,
                                                    mb: 2,
                                                    fontFamily: "Outfit, sans-serif",
                                                    padding: "12px",
                                                    backgroundColor: "#f8f9fa",
                                                    borderRadius: "6px",
                                                    borderLeft: "4px solid #0E73F6",
                                                    minHeight: "60px",
                                                    lineHeight: "1.5"
                                                }}>
                                                    {rowData?.descriptionOfIncident ?? "N/A"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Time of Incident:</strong>
                                                </Typography>
                                                <Typography sx={valueStyle}>
                                                    {formatTime(rowData?.timeOfIncident)}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Date of Incident:</strong>
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                                    <Typography sx={valueStyle}>
                                                        {rowData?.dateOfIncident ?
                                                            moment(rowData.dateOfIncident).format("DD MMM YYYY") :
                                                            "N/A"}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Incident Severity Rating:</strong>
                                                </Typography>
                                                <Chip
                                                    label={rowData?.incidentSeverityRating}
                                                    sx={{
                                                        backgroundColor: "#fff3e0",
                                                        color: "#f57c00",
                                                        fontWeight: 500
                                                    }}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Personnel Involved:</strong>
                                                </Typography>
                                                <Typography sx={valueStyle}>
                                                    {rowData?.personnelInvolved ?? "N/A"}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Status:</strong>
                                                </Typography>
                                                <Chip
                                                    label={rowData?.status}
                                                    sx={valueStyle}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Injury Details:</strong>
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: "15px",
                                                    color: "#111827",
                                                    fontWeight: 500,
                                                    mb: 2,
                                                    fontFamily: "Outfit, sans-serif",
                                                    padding: "8px",
                                                    backgroundColor: "#f8f9fa",
                                                    borderRadius: "6px",
                                                    minHeight: "40px",
                                                    lineHeight: "1.5"
                                                }}>
                                                    {rowData?.injuryDetails ?? "N/A"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>LTI Days Lost:</strong>
                                                </Typography>
                                                <Typography sx={valueStyle}>
                                                    {rowData?.ltiDaysLost ?? "N/A"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Immediate Action Taken:</strong>
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: "15px",
                                                    color: "#111827",
                                                    fontWeight: 500,
                                                    mb: 2,
                                                    fontFamily: "Outfit, sans-serif",
                                                    padding: "8px",
                                                    backgroundColor: "#f8f9fa",
                                                    borderRadius: "6px",
                                                    minHeight: "40px",
                                                    lineHeight: "1.5"
                                                }}>
                                                    {rowData?.immediateActionTaken ?? "N/A"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Root Cause Identified:</strong>
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: "15px",
                                                    color: "#111827",
                                                    fontWeight: 500,
                                                    mb: 2,
                                                    fontFamily: "Outfit, sans-serif",
                                                    padding: "8px",
                                                    backgroundColor: "#f8f9fa",
                                                    borderRadius: "6px",
                                                    minHeight: "40px",
                                                    lineHeight: "1.5"
                                                }}>
                                                    {rowData?.rootCauseIdentified ?? "N/A"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Corrective and Preventive Action:</strong>
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: "16px",
                                                    color: "#111827",
                                                    fontWeight: 500,
                                                    mb: 2,
                                                    fontFamily: "Outfit, sans-serif",
                                                    padding: "12px",
                                                    backgroundColor: "#f8f9fa",
                                                    borderRadius: "6px",
                                                    borderLeft: "4px solid #4CAF50",
                                                    minHeight: "60px",
                                                    lineHeight: "1.5"
                                                }}>
                                                    {rowData?.correctiveAndPreventiveAction ?? "N/A"}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Responsible Person:</strong>
                                                </Typography>
                                                <Typography sx={valueStyle}>
                                                    {rowData?.responsiblePerson}
                                                </Typography>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Date of Closure:</strong>
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                                    <Typography sx={valueStyle}>
                                                        {rowData?.dateOfClosure ?
                                                            moment(rowData.dateOfClosure).format("DD MMM YYYY") :
                                                            "N/A"}
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>CAPA Status:</strong>
                                                </Typography>
                                                <Chip
                                                    label={rowData?.capaStatus}
                                                    sx={valueStyle}
                                                />
                                            </Grid>

                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                <Typography sx={labelStyle}>
                                                    <strong>Verification Done By:</strong>
                                                </Typography>
                                                <Typography sx={valueStyle}>
                                                    {rowData?.verificationDoneBy ?? "N/A"}
                                                </Typography>
                                            </Grid>

                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default ViewIncident;